import Papa from "papaparse";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const PHONE_COLUMN_NAMES = new Set([
  "phone",
  "phonenumber",
  "phone number",
  "mobile",
  "tel",
  "telephone",
  "cell",
]);

export function normalizePhoneForCsvMatch(raw: string | undefined | null): string | undefined {
  const trimmed = raw?.trim();
  if (!trimmed) return undefined;

  try {
    const parsed = parsePhoneNumberFromString(trimmed, "US");
    if (parsed?.number) return parsed.number;
  } catch (error) {
    console.error(`Error parsing phone number ${trimmed}:`, error);
  }

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length >= 10) return digits;

  return undefined;
}

function findPhoneColumnIndex(headers: string[]): number | null {
  for (let i = 0; i < headers.length; i++) {
    if (PHONE_COLUMN_NAMES.has(headers[i].trim().toLowerCase())) {
      return i;
    }
  }
  if (headers.length === 1) return 0;
  return null;
}

function extractPhonesFromRow({
  row,
  phoneColumnIndex,
}: {
  row: string[];
  phoneColumnIndex: number | null;
}): string[] {
  if (phoneColumnIndex != null) {
    const normalized = normalizePhoneForCsvMatch(row[phoneColumnIndex]);
    return normalized ? [normalized] : [];
  }

  const phones: string[] = [];
  for (const cell of row) {
    const normalized = normalizePhoneForCsvMatch(cell);
    if (normalized) phones.push(normalized);
  }
  return phones;
}

export function parsePhoneNumbersFromCsv(text: string): {
  phones: Set<string>;
  parsedCount: number;
  skippedCount: number;
} {
  const parsed = Papa.parse<string[]>(text, { skipEmptyLines: true });
  const rows = parsed.data.filter((row) => row.some((cell) => cell?.trim()));

  if (rows.length === 0) {
    return { phones: new Set(), parsedCount: 0, skippedCount: 0 };
  }

  const firstRow = rows[0] ?? [];
  const firstRowHasHeader = firstRow.some((cell) =>
    PHONE_COLUMN_NAMES.has(cell.trim().toLowerCase()),
  );
  const headers = firstRowHasHeader ? firstRow : firstRow.map((_, i) => `column_${i}`);
  const dataRows = firstRowHasHeader ? rows.slice(1) : rows;
  const phoneColumnIndex = findPhoneColumnIndex(headers);

  const phones = new Set<string>();
  let skippedCount = 0;

  for (const row of dataRows) {
    const extracted = extractPhonesFromRow({ row, phoneColumnIndex });
    if (extracted.length === 0) {
      skippedCount += 1;
      continue;
    }
    for (const phone of extracted) {
      phones.add(phone);
    }
  }

  return { phones, parsedCount: phones.size, skippedCount };
}

export function leadMatchesCsvPhone({
  leadPhone,
  csvPhones,
}: {
  leadPhone: string | undefined;
  csvPhones: Set<string>;
}): boolean {
  const normalized = normalizePhoneForCsvMatch(leadPhone);
  if (!normalized) return false;
  if (csvPhones.has(normalized)) return true;

  const digits = normalized.replace(/\D/g, "");
  if (digits.length >= 10) {
    for (const csvPhone of csvPhones) {
      if (csvPhone.replace(/\D/g, "").endsWith(digits.slice(-10))) {
        return true;
      }
    }
  }

  return false;
}
